import React, { useMemo, useState } from 'react';
import { useMoneyFormat, useMonthFormat } from '../../context/LocaleContext';
import { formatAccounting } from '../../domain/expenses';
import s from './IncomeTrend.module.css';

// Ten-month income trend: four series over time, so a line chart.
//
// The window straddles today — six months of history, the selected month, then
// three months of forecast drawn dashed. The dashing is the whole contract with
// the reader: solid happened, dashed might. Encoding it as line style rather
// than a fifth colour keeps each series one colour across both halves, so the
// eye follows Teaching from March into October without re-learning the legend.
//
// Palette is the validated categorical order (blue → orange → aqua → yellow),
// assigned in fixed slot order and never cycled. Colour follows the entity, so
// Net Income stays blue whether or not the other series have any data. The
// four are the `chart` tokens (design/tokens.json), which both phones draw too.
//
// The palette's two lighter hues fall under 3:1 against the surface, which
// obliges visible labels rather than colour alone — hence the always-present
// legend and the crosshair readout, which names every series it reports.
const SERIES = [
  { key: 'netIncomeMinor',    label: 'Net Income',    color: 'var(--chart-net-income)' },
  { key: 'teachingMinor',     label: 'Teaching',      color: 'var(--chart-teaching)' },
  { key: 'otherRevenueMinor', label: 'Other Revenue', color: 'var(--chart-other-revenue)' },
  { key: 'expensesMinor',     label: 'Expenses',      color: 'var(--chart-expenses)' },
];

const W = 1020, H = 260, PAD = { top: 14, right: 24, bottom: 30, left: 66 };

// `read`: what the series is folded from has answered (#629). Until then the
// chart does not say nothing was recorded.
export default function IncomeTrend({ series, read = true }) {
  const [hover, setHover] = useState(null);
  const formatMoney = useMoneyFormat();
  // "September 2026" in the studio's locale, not the browser's. Still built on
  // the UTC grid inside formatMonthYear — these are UTC midnights, and reading
  // them locally shifts every tick back a month.
  const monthLabel = useMonthFormat();

  const { paths, ticks, x, y, hasData } = useMemo(() => {
    const values = series.flatMap((r) => SERIES.map((line) => r[line.key]));
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    // A flat all-zero chart still needs a sane band, or every point lands on one
    // line and the axis reads as broken rather than empty.
    const span = max - min || 10000;

    // Round the axis out to a human step — $20, $50, $100 — instead of labelling
    // whatever the data's extremes happen to be. Ticks a reader can do
    // arithmetic on are the difference between reading a value off the grid and
    // having to hover for it.
    const rawStep = span / 8;
    const mag = 10 ** Math.floor(Math.log10(rawStep || 1));
    const step = [1, 2, 2.5, 5, 10].map((f) => f * mag).find((c) => c >= rawStep) || mag * 10;
    const lo = Math.floor(min / step) * step;
    const hi = Math.ceil(max / step) * step;
    const axis = [];
    for (let t = lo; t <= hi + step / 2; t += step) axis.push(Math.round(t));

    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    const xf = (i) => PAD.left + (series.length <= 1 ? plotW / 2 : (i / (series.length - 1)) * plotW);
    const yf = (v) => PAD.top + plotH - ((v - lo) / ((hi - lo) || 1)) * plotH;

    // The forecast tail is drawn as its own dashed path. It starts at the last
    // actual point rather than the first projected one, so the two halves join
    // instead of leaving a gap at the boundary.
    const firstProjected = series.findIndex((r) => r.projected);
    const solidEnd = firstProjected === -1 ? series.length : firstProjected;
    const seg = (s, from, to) => series.slice(from, to)
      .map((r, k) => `${k ? 'L' : 'M'}${xf(from + k).toFixed(1)},${yf(r[s.key]).toFixed(1)}`)
      .join(' ');

    return {
      hasData: values.some((v) => v !== 0),
      x: xf,
      y: yf,
      ticks: axis,
      paths: SERIES.map((line) => ({
        ...line,
        solid: solidEnd > 0 ? seg(line, 0, solidEnd) : '',
        dashed: firstProjected === -1 ? '' : seg(line, Math.max(0, solidEnd - 1), series.length),
      })),
    };
  }, [series]);

  return (

      <div className={s.trend}>
        <svg className={s.svg} viewBox={`0 0 ${W} ${H}`} role="img"
             aria-label="Monthly net income, teaching income, other revenue and expenses">
          {ticks.map((t, i) => (
            <g key={i}>
              <line className={s.grid} x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} />
              <text className={s.axis} x={PAD.left - 8} y={y(t) + 3} textAnchor="end">
                {formatAccounting(t, formatMoney)}
              </text>
            </g>
          ))}

          {/* Every point is labelled, as MMS does. The labels are long, so they
              are given room by widening the viewBox rather than being thinned —
              dropping every other tick makes a 10-month trend hard to read. */}
          {series.map((r, i) => (
            <text key={r.yearMonth} className={s.axis} x={x(i)} y={H - 8} textAnchor="middle">
              {monthLabel(r.yearMonth)}
            </text>
          ))}

          {paths.map((p) => (
            <g key={p.key}>
              {p.solid ? (
                <path d={p.solid} fill="none" style={{ stroke: p.color }}
                      strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              ) : null}
              {p.dashed ? (
                <path d={p.dashed} fill="none" style={{ stroke: p.color }} strokeWidth="2"
                      strokeDasharray="6 5" strokeLinejoin="round" strokeLinecap="round" />
              ) : null}
            </g>
          ))}

          {/* A marker on every point, as MMS draws them: with four series
              crossing each other the vertices are what separate one line's path
              from another where they overlap. */}
          {SERIES.map((line) => series.map((r, i) => (
            <circle key={`${line.key}-${i}`} cx={x(i)} cy={y(r[line.key])} r="3.5"
                    style={{ fill: line.color }} stroke="#fff" strokeWidth="1.5" />
          )))}

          {hover != null ? (
            <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom}
                  stroke="var(--color-gray-300)" strokeWidth="1" />
          ) : null}

          {paths.map((p) => (
            hover != null ? (
              <circle key={p.key} cx={x(hover)} cy={y(series[hover][p.key])} r="4.5"
                      style={{ fill: p.color }} stroke="#fff" strokeWidth="2" />
            ) : null
          ))}

          {/* Hit targets wider than the marks, so hovering does not require
              landing on a 2px line. */}
          {series.map((r, i) => (
            <rect key={r.yearMonth} x={x(i) - 18} y={PAD.top} width="36" height={H - PAD.top - PAD.bottom}
                  fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
          ))}
        </svg>

        {hover != null ? (
          <div className={s.tip} style={{ left: `${(x(hover) / W) * 100}%` }}>
            <div className={s.tipHead}>
              {monthLabel(series[hover].yearMonth)}
              {series[hover].projected ? (
                <span className={s.tipProjected}> · projected</span>
              ) : null}
            </div>
            {SERIES.map((line) => (
              <div className={s.tipRow} key={line.key}>
                <span className={s.tipLabelGroup}>
                  <span className={s.dot} style={{ background: line.color }} />
                  <span className={s.tipLabel}>{line.label}</span>
                </span>
                <span className={s.tipValue}>
                  {formatAccounting(series[hover][line.key], formatMoney)}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        <div className={s.legend}>
          {SERIES.map((line) => (
            <span className={s.key} key={line.key}>
              <span className={s.dot} style={{ background: line.color }} />
              {line.label}
            </span>
          ))}
        </div>

        {!hasData && read ? (
          <div className={s.note}>
            No income or expenses recorded in this period yet.
          </div>
        ) : null}
    </div>
  );
}
